/**
 * RouteFormWizard.jsx
 * Formulario de Rota em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Nome, Dispositivo, Buffer
 * 2. Rota - Origem e Destino (CreateRoute)
 * 3. Revisao
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Route as RouteIcon,
  Map as MapIcon,
  CheckCircle as ReviewIcon,
  DirectionsCar as DevicesIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import SearchableSelect from '../../common/components/SearchableSelect';
import CreateRoute from '../../common/components/CreateRoute';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors, devices }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SearchableSelect
            options={devices}
            value={data.deviceId}
            onChange={(value) => updateData({ deviceId: value })}
            label={t('deviceTitle')}
            required
            error={!!errors?.deviceId}
            helperText={errors?.deviceId}
            startAdornment={<DevicesIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            label={t('routeBuffer') || 'Buffer'}
            value={data.bufferMeters || 50}
            onChange={(e) => updateData({ bufferMeters: Number(e.target.value) })}
            InputProps={{
              endAdornment: <InputAdornment position="end">m</InputAdornment>,
            }}
            inputProps={{ min: 1, max: 1000 }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('routeBufferHelp') || 'O buffer define a tolerancia em metros para considerar que o dispositivo esta seguindo a rota.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 2: DEFINICAO DA ROTA
// ============================================================================

const RouteStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleRouteChange = (origin, destination, routeData) => {
    updateData({
      origin,
      destination,
      area: routeData?.area,
      routeInfo: routeData?.route ? {
        distance: routeData.route.distance,
        duration: routeData.route.duration,
      } : null,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('routeCreateHelp') || 'Defina a origem e o destino da rota. Voce pode arrastar os marcadores no mapa ou digitar os enderecos.'}
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: tokens?.radius?.lg || '12px',
          overflow: 'hidden',
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <CreateRoute
          deviceId={data.deviceId}
          bufferMeters={data.bufferMeters}
          origin={data.origin}
          destination={data.destination}
          onChange={handleRouteChange}
        />
      </Box>

      {(data.origin || data.destination) && (
        <Grid container spacing={2}>
          {data.origin && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: tokens?.radius?.md || '8px',
                  backgroundColor: colors.surfaceHover,
                  border: `1px solid ${colors.borderSubtle}`,
                }}
              >
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {t('routeOrigin') || 'Origem'}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                  {data.origin.lat?.toFixed(6)}, {data.origin.lng?.toFixed(6)}
                </Typography>
              </Box>
            </Grid>
          )}
          {data.destination && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: tokens?.radius?.md || '8px',
                  backgroundColor: colors.surfaceHover,
                  border: `1px solid ${colors.borderSubtle}`,
                }}
              >
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {t('routeDestination') || 'Destino'}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                  {data.destination.lat?.toFixed(6)}, {data.destination.lng?.toFixed(6)}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data, devices }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const device = devices.find((d) => d.id === data.deviceId);
  const hasRoute = data.origin && data.destination;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      {/* Aviso se nao tem rota definida */}
      {!hasRoute && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            backgroundColor: colors.warningBg || 'rgba(210, 153, 34, 0.15)',
            borderRadius: tokens?.radius?.md || '8px',
            border: `1px solid ${colors.warning || '#d29922'}40`,
          }}
        >
          <MapIcon sx={{ color: colors.warning || '#d29922' }} />
          <Typography sx={{ color: colors.textPrimary, fontSize: '13px' }}>
            {t('routeNotDefined') || 'A rota ainda nao foi definida. Voce pode definir depois na edicao.'}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('deviceTitle')} value={device?.name || '-'} />
        <InfoRow label={t('routeBuffer') || 'Buffer'} value={`${data.bufferMeters || 50}m`} />
      </Box>

      {hasRoute && (
        <Box
          sx={{
            backgroundColor: colors.surfaceElevated,
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${colors.borderSubtle}`,
            p: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {t('sharedRoute') || 'Rota'}
          </Typography>
          <InfoRow
            label={t('routeOrigin') || 'Origem'}
            value={`${data.origin.lat?.toFixed(6)}, ${data.origin.lng?.toFixed(6)}`}
          />
          <InfoRow
            label={t('routeDestination') || 'Destino'}
            value={`${data.destination.lat?.toFixed(6)}, ${data.destination.lng?.toFixed(6)}`}
          />
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RouteFormWizard = ({ route, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);

  // Fetch devices (all=true para admins verem todos)
  useEffectAsync(async () => {
    try {
      const response = await fetch('/api/devices?all=true');
      if (response.ok) {
        setDevices(await response.json());
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  }, []);

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardRouteBasicDescription') || 'Nome e dispositivo',
      icon: <RouteIcon sx={{ fontSize: 18 }} />,
      component: (props) => <BasicInfoStep {...props} devices={devices} />,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (!data.deviceId) errors.deviceId = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('sharedRoute') || 'Rota',
      title: t('sharedRoute') || 'Definir Rota',
      description: t('wizardRouteMapDescription') || 'Origem e destino',
      icon: <MapIcon sx={{ fontSize: 18 }} />,
      component: RouteStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: (props) => <ReviewStep {...props} devices={devices} />,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = route?.id ? `/api/routes/${route.id}` : '/api/routes';
      const method = route?.id ? 'PUT' : 'POST';

      // Converter campos frontend para backend
      // NOTA: Não enviamos campos com valor null - deixamos undefined para não serem serializados
      const payload = {
        name: data.name,
        bufferMeters: data.bufferMeters || 50,
      };

      // Só adiciona campos opcionais se tiverem valor
      if (data.deviceId) payload.deviceId = data.deviceId;
      if (data.area) payload.area = data.area;
      if (data.origin?.lat) payload.originLat = data.origin.lat;
      if (data.origin?.lng) payload.originLon = data.origin.lng;
      if (data.destination?.lat) payload.destinationLat = data.destination.lat;
      if (data.destination?.lng) payload.destinationLon = data.destination.lng;

      // Adiciona id apenas se estiver editando
      if (route?.id) {
        payload.id = route.id;
      }
      // geofenceId é gerenciado pelo backend - não enviamos

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('Route payload:', payload);

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/routes');
      } else {
        const errorText = await response.text();
        console.error('Save error response:', response.status, errorText);
        throw new Error(errorText || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/routes');
  };

  // Converter dados do backend para formato do frontend
  const initialData = route ? {
    name: route.name || '',
    deviceId: route.deviceId || null,
    bufferMeters: route.bufferMeters || 50,
    area: route.area || '',
    origin: route.originLat && route.originLon ? {
      lat: route.originLat,
      lng: route.originLon,
    } : null,
    destination: route.destinationLat && route.destinationLon ? {
      lat: route.destinationLat,
      lng: route.destinationLon,
    } : null,
  } : {
    name: '',
    deviceId: null,
    bufferMeters: 50,
    area: '',
    origin: null,
    destination: null,
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={route?.id ? null : 'route-wizard-draft'}
      completeButtonText={route?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default RouteFormWizard;
